import React, { useState, useEffect, useMemo, useCallback, useRef } from 'react';
import { Card, CardBody, Badge, Spinner as BootstrapSpinner } from 'reactstrap'; // Renamed Spinner
import { Icon } from '../../../../components/Component';
import useAxiosPrivate from '../../../../hooks/useAxiosPrivate';
import { format, addDays, subDays, startOfDay, endOfDay, parseISO, isToday } from 'date-fns';
import { ActivityStatus } from '../../../../utils/envConfig';

// CSS for hiding scrollbars
const kanbanStyles = `
  .kanban-column-content::-webkit-scrollbar {
    width: 0px;
    height: 0px;
  }
  .kanban-column-content::-webkit-scrollbar-thumb {
    background: transparent;
  }
  .kanban-column-content::-webkit-scrollbar-track {
    background: transparent;
  }
`;

function useInView() {
  const [isInView, setIsInView] = useState(false);
  const ref = useRef(null);

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        setIsInView(entry.isIntersecting);
      },
      {
        threshold: 0.1, // Trigger when 10% of the element is visible
      }
    );

    if (ref.current) {
      observer.observe(ref.current);
    }

    return () => {
      if (ref.current) {
        observer.unobserve(ref.current);
      }
    };
  }, []); // Empty dependency array ensures this runs only once on mount and cleanup on unmount

  return [ref, isInView];
}

// Get activity icon

const getActivityIcon = (type) => {

  if (type.includes('Deal')) return 'check-circle';

  if (type.includes('Listing')) return 'home';

  if (type.includes('Lead')) return 'user-plus';

  if (type.includes('Viewing')) return 'eye';

  if (type.includes('Call')) return 'phone';

  if (type.includes('Inspection')) return 'search';

  if (type.includes('Offer')) return 'file-text';

  if (type.includes('WhatsApp')) return 'message-circle';

  if (type.includes('Email')) return 'mail';

  if (type.includes('SMSMessage')) return 'message-square';

  if (type.includes('Appointment')) return 'calendar';

  return 'activity';

};

const getActivityIconBg = (type) => {
  // Helper for opacity classes
  const subtleBg = (color) => `bg-${color}-subtle text-${color}-emphasis`;

  if (type.includes('Deal')) return subtleBg('success');
  if (type.includes('Listing')) return subtleBg('primary');
  if (type.includes('Lead')) return subtleBg('info');
  if (type.includes('Viewing')) return subtleBg('warning');
  if (type.includes('Call')) return subtleBg('danger'); // Assuming 'pink' maps to 'danger'
  if (type.includes('Inspection')) return 'bg-purple-subtle text-purple-emphasis'; // Custom: needs .bg-purple-subtle etc. defined, or map to theme
  if (type.includes('Offer')) return 'bg-indigo-subtle text-indigo-emphasis'; // Custom: needs .bg-indigo-subtle etc. defined, or map to theme
  if (type.includes('WhatsApp')) return subtleBg('success');
  if (type.includes('Email')) return subtleBg('info');
  if (type.includes('SMSMessage')) return subtleBg('warning');
  if (type.includes('Appointment')) return subtleBg('primary');
  return 'bg-light text-dark'; // Default: standard light background
};
// Note on custom colors like purple/indigo:
// If 'purple' and 'indigo' are not part of your Bootstrap theme,
// you'll need to define CSS classes like:
// .bg-purple-subtle { background-color: var(--bs-purple-bg-subtle); } /* If using CSS vars */
// .text-purple-emphasis { color: var(--bs-purple-text-emphasis); }
// For simplicity, I might map them to standard Bootstrap colors (e.g., primary, secondary) in the code below if these custom styles aren't assumed.
// Let's map purple to 'secondary' and indigo to 'primary' for now if they aren't standard.
// Revised for standard BS colors:
const getStandardActivityIconBg = (type) => {
  const subtleBg = (color) => `bg-${color}-subtle text-${color}-emphasis`;

  if (type.includes('Deal')) return subtleBg('success');
  if (type.includes('Listing')) return subtleBg('primary');
  if (type.includes('Lead')) return subtleBg('info');
  if (type.includes('Viewing')) return subtleBg('warning');
  if (type.includes('Call')) return subtleBg('danger'); // Pink -> Danger
  if (type.includes('Inspection')) return subtleBg('secondary'); // Purple -> Secondary
  if (type.includes('Offer')) return subtleBg('primary'); // Indigo -> Primary variant
  if (type.includes('WhatsApp')) return subtleBg('success');
  if (type.includes('Email')) return subtleBg('info');
  if (type.includes('SMSMessage')) return subtleBg('warning');
  if (type.includes('Appointment')) return subtleBg('primary');
  return 'bg-light text-dark border';
};


// Get status badge style (Bootstrap 5.3 subtle style)
const getStatusBadge = (status) => {
  const baseClass = "badge rounded-pill border"; // Common classes for all badges
  switch (status) {
    case ActivityStatus.Pending:
      return `${baseClass} bg-danger-subtle text-danger-emphasis border-danger-subtle`;
    case ActivityStatus.Overdue:
      return `${baseClass} bg-warning-subtle text-warning-emphasis border-warning-subtle`;
    case ActivityStatus.Complete:
      return `${baseClass} bg-success-subtle text-success-emphasis border-success-subtle`;
    case ActivityStatus.AutoCompleted:
      return `${baseClass} bg-info-subtle text-info-emphasis border-info-subtle`;
    case ActivityStatus.Skipped:
      return `${baseClass} bg-secondary-subtle text-secondary-emphasis border-secondary-subtle`; // Mapped 'info' to 'secondary'
    default:
      return `${baseClass} bg-light text-dark border-light-subtle`; // Standard light badge
  }
};

// Helper functions (assuming they are defined above as per previous response, using getStandardActivityIconBg)
// getActivityIcon, getStandardActivityIconBg, getStatusBadge

const KanbanActivityView = ({ userId }) => {
  const [myRef, isInView] = useInView();
  const [dayActivities, setDayActivities] = useState({});
  const [dayPagination, setDayPagination] = useState({});
  const [dayLoading, setDayLoading] = useState({});
  const [dayLoadingMore, setDayLoadingMore] = useState({});
  const [pageSize] = useState(10);
  const axios = useAxiosPrivate();
  const todayDate = useMemo(() => new Date(), []); // Memoize today's date object

  const timelineRef = useRef(null);
  const scrollLeftButtonRef = useRef(null);
  const scrollRightButtonRef = useRef(null);

  const COLUMN_WIDTH = 280; // px
  const COLUMN_GAP = 16; // px (corresponds to Bootstrap's gap-3, which is 1rem)

  // Define date ranges: 7 days, Today is 3rd column, Yesterday is 2nd.
  const dateRanges = useMemo(() => {
    const ranges = [];
    for (let i = -2; i <= 4; i++) { // Today will be at index 2 (i=0)
      const targetDate = addDays(todayDate, i);
      ranges.push({
        id: `day${i === 0 ? '-today' : (i < 0 ? `${i}` : `+${i}`)}`, // Unique ID, e.g. day-today, day-1, day+1
        title: isToday(targetDate) ? 'Today' : format(targetDate, 'EEE, MMM d'),
        start: startOfDay(targetDate),
        end: endOfDay(targetDate),
        isTodayColumn: isToday(targetDate),
      });
    }
    return ranges;
  }, [todayDate]);

  const fetchDayActivities = useCallback(async (dayId, dateRange, page = 1, append = false) => {
    if (!userId) return;
    try {
      if (!append) {
        setDayLoading(prev => ({ ...prev, [dayId]: true }));
      } else {
        setDayLoadingMore(prev => ({ ...prev, [dayId]: true }));
      }

      const query = { page, pageSize };
      const payload = {
        date: [dateRange.start.toISOString(), dateRange.end.toISOString()],
        agentIdBody: userId,
      };

      const res = await axios.post('/activities/agent', payload, { params: query });

      if (res.data) {
        const activities = res.data.activities || [];
        const totalItems = res.data.totalItems || 0;
        // hasMore should check if current items < totalItems AND new items were fetched
        const hasMore = (page * pageSize) < totalItems && activities.length > 0;


        setDayActivities(prev => ({
          ...prev,
          [dayId]: append ? [...(prev[dayId] || []), ...activities] : activities
        }));
        setDayPagination(prev => ({
          ...prev,
          [dayId]: { currentPage: page, totalItems, hasMore }
        }));
      }
    } catch (error) {
      console.error(`Error fetching activities for ${dayId}:`, error);
      // Keep existing data on error, maybe set an error state
      setDayPagination(prev => ({
        ...prev,
        [dayId]: { ...(prev[dayId] || { currentPage: 0, totalItems: 0 }), hasMore: false } // Stop pagination on error
      }));
    } finally {
      if (!append) {
        setDayLoading(prev => ({ ...prev, [dayId]: false }));
      } else {
        setDayLoadingMore(prev => ({ ...prev, [dayId]: false }));
      }
    }
  }, [axios, pageSize, userId]);

  const loadMoreDayActivities = useCallback(async (dayId, dateRange) => {
    const pagination = dayPagination[dayId] || { currentPage: 0, hasMore: true };
    if (!pagination.hasMore || dayLoadingMore[dayId] || dayLoading[dayId]) return;
    const nextPage = pagination.currentPage + 1;
    await fetchDayActivities(dayId, dateRange, nextPage, true);
  }, [dayPagination, dayLoadingMore, dayLoading, fetchDayActivities]);

  useEffect(() => {
    const initializeAllDays = async () => {
      if (!userId) return;
      const initialLoadingState = {};
      dateRanges.forEach(range => { initialLoadingState[range.id] = true; });
      setDayLoading(initialLoadingState);

      await Promise.all(dateRanges.map(range =>
        fetchDayActivities(range.id, range, 1, false)
      ));
    };
    initializeAllDays();
  }, [userId, dateRanges, fetchDayActivities]); // fetchDayActivities is memoized

  const groupedActivities = useMemo(() => dateRanges.map(range => ({
    ...range,
    activities: dayActivities[range.id] || [],
    pagination: dayPagination[range.id] || { hasMore: false, currentPage: 1, totalItems: 0 },
    loading: dayLoading[range.id] || false,
    loadingMore: dayLoadingMore[range.id] || false
  })), [dateRanges, dayActivities, dayPagination, dayLoading, dayLoadingMore]);

  const handleScroll = (direction) => {
    if (timelineRef.current) {
      const scrollAmount = direction === 'left' ? - (COLUMN_WIDTH + COLUMN_GAP) * 2 : (COLUMN_WIDTH + COLUMN_GAP) * 2; // Scroll by 2 columns
      timelineRef.current.scrollBy({ left: scrollAmount, behavior: 'smooth' });
    }
  };

  // Effect for initial scroll and button visibility updates
  useEffect(() => {
    const timelineElement = timelineRef.current;
    if (!timelineElement) return;

    // Initial scroll to make "Yesterday" (index 1) the first column
    const yesterdayColumnIndex = dateRanges.findIndex(range => range.id === 'day-1'); // Yesterday is now day-1
    console.log("yester")
    if (yesterdayColumnIndex > 0) { // ensure yesterday is not the very first in array
      timelineElement.scrollLeft = yesterdayColumnIndex * (COLUMN_WIDTH + COLUMN_GAP);
    } else {
      timelineElement.scrollLeft = 0; // if yesterday is the first, or not found, scroll to start
    }


    const updateScrollButtonsVisibility = () => {
      if (!timelineElement || !scrollLeftButtonRef.current || !scrollRightButtonRef.current) return;
      const { scrollLeft, scrollWidth, clientWidth } = timelineElement;
      const canScrollLeft = scrollLeft > 20;
      const canScrollRight = scrollLeft < (scrollWidth - clientWidth - 20);

      scrollLeftButtonRef.current.style.opacity = canScrollLeft ? '1' : '0.3';
      scrollLeftButtonRef.current.disabled = !canScrollLeft;
      scrollRightButtonRef.current.style.opacity = canScrollRight ? '1' : '0.3';
      scrollRightButtonRef.current.disabled = !canScrollRight;
    };

    updateScrollButtonsVisibility(); // Initial check
    timelineElement.addEventListener('scroll', updateScrollButtonsVisibility);

    // Also listen to window resize if layout is responsive
    window.addEventListener('resize', updateScrollButtonsVisibility);

    return () => {
      timelineElement.removeEventListener('scroll', updateScrollButtonsVisibility);
      window.removeEventListener('resize', updateScrollButtonsVisibility);
    };
  }, [dateRanges, isInView]); // Rerun when groupedActivities data changes (content might affect scrollWidth)

  return (
    <>
      <style>{kanbanStyles}</style>
      <Card className="shadow-sm border-0 h-100" style={{ maxHeight: '800px', overflow: 'hidden' }}> {/* Use maxHeight if preferred */}
        <CardBody className="p-3 p-md-4 d-flex flex-column h-100 overflow-hidden">
          <div ref={myRef} className="mb-3">
            <h5 className="card-title fw-semibold mb-1 d-flex align-items-center">
              <Icon
                name="calendar-alt"
                className="me-2 text-primary"
                style={{
                  lineHeight: 1,
                  display: 'inline-flex',
                  alignItems: 'center',
                  justifyContent: 'center'
                }}
              />
              7-Day Activity View
            </h5>
            <p className="text-muted small mb-0">
              Activities from {format(dateRanges[0].start, 'MMM d')} to {format(dateRanges[dateRanges.length - 1].start, 'MMM d, yyyy')}
            </p>
          </div>

          <div className="position-relative flex-grow-1 d-flex flex-column overflow-hidden">
            {/* Scroll Buttons */}
            <button
              ref={scrollLeftButtonRef}
              className="btn btn-light btn-sm rounded-circle shadow-sm position-absolute top-50 start-0 translate-middle-y z-2 p-0 d-flex align-items-center justify-content-center"
              style={{ width: '32px', height: '32px', opacity: '0.3', transition: 'opacity 0.2s ease' }}
              onClick={() => handleScroll('left')}
              aria-label="Scroll left"
            >
              <Icon
                name="chevron-left"
                style={{
                  lineHeight: 1,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center'
                }}
              />
            </button>
            <button
              ref={scrollRightButtonRef}
              className="btn btn-light btn-sm rounded-circle shadow-sm position-absolute top-50 end-0 translate-middle-y z-2 p-0 d-flex align-items-center justify-content-center"
              style={{ width: '32px', height: '32px', opacity: '0.3', transition: 'opacity 0.2s ease' }}
              onClick={() => handleScroll('right')}
              aria-label="Scroll right"
            >
              <Icon
                name="chevron-right"
                style={{
                  lineHeight: 1,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center'
                }}
              />
            </button>

            {/* Horizontal Scroll Area */}
            <div
              ref={timelineRef}
              className="flex-grow-1 overflow-x-auto overflow-y-hidden"
              style={{ scrollBehavior: 'smooth' }} // scrollbarWidth: 'none' for some browsers if you want to hide it
            >
              <ul className="list-unstyled d-flex h-100 m-0 p-0 py-2 gap-3 "> {/* py-2 for vertical padding, gap-3 for column spacing */}
                {groupedActivities.map((column) => (
                  <li key={column.id} className="d-flex flex-column h-100" style={{ minWidth: `${COLUMN_WIDTH}px` }}>
                    {/* Column Header */}
                    <div
                      className={`p-2 rounded text-center mb-2 border d-flex align-items-center justify-content-center ${column.isTodayColumn ? 'bg-primary-subtle text-primary-emphasis border-primary-subtle shadow-sm' : 'bg-light'}`}
                    >
                      <Icon
                        name="calendar-check"
                        size="sm"
                        className={`me-1 ${column.isTodayColumn ? 'text-primary' : 'text-muted'}`}
                        style={{
                          lineHeight: 1,
                          display: 'inline-flex',
                          alignItems: 'center',
                          justifyContent: 'center'
                        }}
                      />
                      <span className={`fw-medium ${column.isTodayColumn ? 'fw-bold' : 'small'}`}>{column.title}</span>
                    </div>

                    {/* Column Content (Activities) */}
                    <div
                      className="flex-grow-1 overflow-y-auto pb-2 kanban-column-content"
                      style={{
                        scrollbarWidth: 'none', /* Firefox */
                        msOverflowStyle: 'none', /* Internet Explorer 10+ */
                      }}
                    > {/* pb-2 to avoid cutting off last item's shadow */}
                      {column.loading && !column.loadingMore ? (
                        <div className="d-flex justify-content-center align-items-center h-100 py-4">
                          <BootstrapSpinner size="sm" color="primary" />
                        </div>
                      ) : column.activities.length === 0 && !column.loadingMore ? (
                        <div className="text-center p-3 mt-2 border-dashed rounded bg-light"> {/* bg-light to make it less stark */}
                          <Icon
                            name="calendar-times"
                            className="text-muted fs-4 mb-1"
                            style={{
                              lineHeight: 1,
                              display: 'inline-flex',
                              alignItems: 'center',
                              justifyContent: 'center'
                            }}
                          />
                          <p className="text-muted small mb-0">No activities</p>
                        </div>
                      ) : (
                        <>
                          {column.activities.map((activity) => (
                            <div
                              key={activity.id}
                              className="p-2 mb-2 border rounded bg-white shadow-sm" // Bootstrap card-like styling
                            >
                              <div className="d-flex align-items-center mb-1">
                                <div
                                  className={`me-2 rounded-circle d-flex align-items-center justify-content-center ${getStandardActivityIconBg(activity.type)}`}
                                  style={{
                                    width: '24px',
                                    height: '24px',
                                    flexShrink: 0,
                                    minWidth: '24px',
                                    minHeight: '24px'
                                  }}
                                >
                                  <Icon
                                    name={getActivityIcon(activity.type)}
                                    style={{
                                      fontSize: '0.7rem',
                                      lineHeight: 1,
                                      display: 'flex',
                                      alignItems: 'center',
                                      justifyContent: 'center'
                                    }}
                                  />
                                </div>
                                <h6 className="mb-0 flex-grow-1 text-truncate small fw-semibold" title={activity.type}>
                                  {activity.type}
                                </h6>
                                <Badge className={`${getStatusBadge(activity.status)} ms-1`} style={{ fontSize: '0.65rem' }}>
                                  {activity.status}
                                </Badge>
                              </div>
                              <div className="ps-1" style={{ marginLeft: '32px' /* Align with title text: 24px icon + 8px margin */ }}>
                                <p className="mb-1 small text-truncate text-dark" title={activity.description || 'No description'}>
                                  {activity.description || <span className="text-muted fst-italic">No description</span>}
                                </p>
                                {activity.user && (
                                  <p className="mb-1 small text-truncate text-muted d-flex align-items-center" title={`${activity.user.first_name} ${activity.user.last_name}`}>
                                    <Icon
                                      name="user-alt"
                                      style={{
                                        fontSize: '0.7rem',
                                        lineHeight: 1,
                                        minWidth: '12px',
                                        display: 'inline-flex',
                                        alignItems: 'center',
                                        justifyContent: 'center'
                                      }}
                                      className="me-1"
                                    />
                                    <span>{activity.user.first_name} {activity.user.last_name}</span>
                                  </p>
                                )}
                                <p className="mb-0 text-muted small d-flex align-items-center">
                                  <Icon
                                    name="clock"
                                    style={{
                                      fontSize: '0.7rem',
                                      lineHeight: 1,
                                      minWidth: '12px',
                                      display: 'inline-flex',
                                      alignItems: 'center',
                                      justifyContent: 'center'
                                    }}
                                    className="me-1"
                                  />
                                  <span>{format(parseISO(activity.date), 'h:mm a')}</span>
                                </p>
                              </div>
                            </div>
                          ))}
                          {column.pagination.hasMore && (
                            <div className="d-flex justify-content-center my-2">
                              <button
                                className="btn btn-sm btn-outline-primary w-100"
                                disabled={column.loadingMore}
                                onClick={() => loadMoreDayActivities(column.id, column)} // Pass full column (contains start/end)
                                style={{ fontSize: '0.75rem' }}
                              >
                                {column.loadingMore ? (
                                  <>
                                    <BootstrapSpinner size="sm" className="me-1" style={{ width: '0.8em', height: '0.8em' }} />
                                    Loading...
                                  </>
                                ) : (
                                  <>
                                    <Icon
                                      name="arrow-down"
                                      style={{
                                        fontSize: '0.7rem',
                                        lineHeight: 1,
                                        display: 'inline-flex',
                                        alignItems: 'center',
                                        justifyContent: 'center'
                                      }}
                                      className="me-1"
                                    />
                                    Load More
                                  </>
                                )}
                              </button>
                            </div>
                          )}
                          {column.activities.length > 0 && (
                            <div className="text-center text-muted mt-2" style={{ fontSize: '0.7rem' }}>
                              {column.activities.length} of {column.pagination.totalItems}
                            </div>
                          )}
                        </>
                      )}
                    </div>
                  </li>
                ))}
              </ul>
            </div>
          </div>
        </CardBody>
      </Card>
    </>
  );
};

export default KanbanActivityView;